import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SlidersHorizontal, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useApi } from '@/hooks/use-api'
import { useLinkedInStatus } from '@/hooks/use-linkedin-status'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { PageHeader } from '@/components/layout/page-header'
import { SectionHelp } from '@/components/help/section-help'
import {
  useLeadSearch,
  useImportLead,
  useBulkImport,
  useEnrichApolloLeads,
  profileToNewLead,
  type SearchFilters,
  type LinkedInProfile,
} from './use-search'
import { SearchFiltersPanel } from './search-filters'
import { CreditBar } from '../credit-bar'
import { SearchResults } from './search-results'
import { AddToCampaignDialog } from '../add-to-campaign-dialog'
import {
  SearchHistory,
  loadSearchHistory,
  saveSearchToHistory,
  clearSearchHistory,
  type SearchHistoryEntry,
} from './search-history'

// --- Import confirmation dialog ---

type ImportDialogProps = {
  readonly profiles: readonly LinkedInProfile[]
  readonly isOpen: boolean
  readonly isLoading: boolean
  readonly error?: string | null
  readonly onConfirm: () => void
  readonly onCancel: () => void
}

function ImportConfirmDialog({ profiles, isOpen, isLoading, error, onConfirm, onCancel }: ImportDialogProps) {
  const count = profiles.length
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle>Import {count} lead{count !== 1 ? 's' : ''}?</DialogTitle>
          <DialogDescription>
            {count === 1 && profiles[0]
              ? `${profiles[0].name} will be added to your pipeline and researched automatically.`
              : `${count} leads will be added to your pipeline and researched automatically.`}
          </DialogDescription>
        </DialogHeader>

        {profiles.length > 0 && profiles.length <= 5 && (
          <ul className="flex flex-col gap-2 text-sm min-w-0 overflow-hidden">
            {profiles.map((p) => (
              <li key={p.id} className="min-w-0 overflow-hidden rounded-md border bg-muted/40 px-3 py-2">
                <p className="font-medium truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[p.title, p.company].filter(Boolean).join(' at ')}
                </p>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={onConfirm} disabled={isLoading} className="w-full">
            {isLoading ? 'Importing...' : 'Import'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={isLoading} className="w-full">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Main page ---

export function LeadDiscoveryPage() {
  const [activeFilters, setActiveFilters] = useState<SearchFilters>({})
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [pendingImport, setPendingImport] = useState<readonly LinkedInProfile[]>([])
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  // After a successful import we offer to drop the freshly-created leads into
  // a campaign. These are DB lead ids (from the import response), not the
  // search-result card ids.
  const [importedLeadIds, setImportedLeadIds] = useState<readonly string[]>([])
  const [isAddCampaignOpen, setIsAddCampaignOpen] = useState(false)
  const [searchHistory, setSearchHistory] = useState<readonly SearchHistoryEntry[]>(
    () => loadSearchHistory(),
  )

  const apiFetch = useApi()

  // Fetch ICP settings to pre-populate search filters and pass user_config for scoring
  const { data: icpData } = useQuery({
    queryKey: ['settings', 'icp'],
    queryFn: async () => {
      type IcpConfig = {
        readonly jobTitles: readonly string[]
        readonly industries: readonly string[]
        readonly locations: readonly string[]
        readonly industryKeywords: string
      }
      const res = await apiFetch<IcpConfig>('/api/settings/icp')
      return res.data ?? null
    },
    staleTime: 5 * 60_000,
  })

  const initialFilters = icpData
    ? {
        title: icpData.jobTitles,
        industries: icpData.industries,
        location: icpData.locations,
      }
    : undefined

  // Pass full ICP config as user_config for backend ICP scoring
  const userConfig = icpData ? (icpData as Record<string, unknown>) : undefined

  // Lead discovery fans out: LinkedIn-native search (free, already enriched)
  // runs alongside the provider-side previews (paid enrich-on-add). LinkedIn
  // results land first so the user has free leads to add immediately while
  // the larger preview list queues up underneath. The page is wrapped in
  // <LinkedInRequiredGate>, so account_id is reliably present here.
  const { data: linkedinStatus } = useLinkedInStatus()
  const accountId = linkedinStatus?.account?.unipile_account_id ?? null
  const { data, isLoading } = useLeadSearch(activeFilters, cursor, userConfig, accountId)
  const importLead = useImportLead()
  const bulkImport = useBulkImport()
  const enrichApollo = useEnrichApolloLeads()

  // Local override map keyed by apollo_id — once the user enriches a preview
  // row, the worker hands back the full enriched profile. We splice that in
  // by id so the card flips from "Preview" to "Import"-ready without a full
  // refetch of the search results (which would re-query Apollo).
  const [enrichedOverrides, setEnrichedOverrides] = useState<
    ReadonlyMap<string, LinkedInProfile>
  >(new Map())
  const [enrichingIds, setEnrichingIds] = useState<ReadonlySet<string>>(new Set())
  const [enrichError, setEnrichError] = useState<string | null>(null)
  // Green confirmation after an enrich batch: tells the user their paid-for
  // leads are already saved in the Leads database (auto-import).
  const [enrichNotice, setEnrichNotice] = useState<string | null>(null)
  // Optimistic in-pipeline overlay. The server marks `in_pipeline` based on
  // the user's leads table, but a fresh import requires the search query to
  // re-run before the flag flips — that lag was visible as a several-second
  // delay before the "Added" badge appeared. We flip locally the instant the
  // user confirms the import and roll back if the request fails.
  const [importedIds, setImportedIds] = useState<ReadonlySet<string>>(new Set())

  const rawResults = data?.results ?? []
  // Apply enrichment overrides — for any preview that's been enriched in this
  // session, swap the row for the enriched version. Match by apollo_id since
  // the preview's card id is the apollo_id and the enriched row's card id is
  // the linkedin_url (they differ — that's why we key the override map by
  // apollo_id explicitly). Then overlay the optimistic-imported flag.
  const results = rawResults.map((r) => {
    let row = r
    if (!row.is_enriched && row.apollo_id) {
      const override = enrichedOverrides.get(row.apollo_id)
      if (override) row = override
    }
    if (importedIds.has(row.id) && !row.in_pipeline) {
      row = { ...row, in_pipeline: true }
    }
    return row
  })
  const nextCursor = data?.next_cursor ?? null
  const searchNote = data?.note ?? null
  const isDemoMode = data?.demo_mode === true
  const apolloError = data?.apollo_error ?? null

  // Save to search history once we have results
  const handleSearch = useCallback((filters: SearchFilters) => {
    setActiveFilters(filters)
    setCursor(null)
    setSelectedIds(new Set())
    setHasSearched(true)
  }, [])

  // Persist history after results arrive
  const handleResultsReceived = useCallback((count: number) => {
    if (!hasSearched) return
    saveSearchToHistory(activeFilters, count)
    setSearchHistory(loadSearchHistory())
  }, [hasSearched, activeFilters])

  // When data arrives, update history
  if (data && hasSearched) {
    const filtersKey = JSON.stringify(activeFilters)
    const historyKey = JSON.stringify(searchHistory[0]?.filters ?? null)
    if (filtersKey !== historyKey) {
      handleResultsReceived(data.results.length)
    }
  }

  function handleSelect(id: string, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  // Page-scoped Select All. The child (SearchResults) computes which IDs are
  // importable on the CURRENT page and passes them up. We add/remove just
  // those from the selection so checking "Select All" on page 1 doesn't
  // also select rows on pages 2-N. Eli flagged that the previous behavior
  // (selecting all 250 across the full result set) broke the bulk-import
  // expectation.
  function handleSelectAll(idsToToggle: readonly string[], selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        for (const id of idsToToggle) next.add(id)
      } else {
        for (const id of idsToToggle) next.delete(id)
      }
      return next
    })
  }

  function handleImportSingle(profile: LinkedInProfile) {
    setPendingImport([profile])
    setIsImportDialogOpen(true)
  }

  function handleBulkImport() {
    // Imports only enriched rows — previews can't be added to the pipeline
    // (no LinkedIn URL means no Unipile target).
    const selected = results.filter((r) => selectedIds.has(r.id) && r.is_enriched)
    setPendingImport(selected)
    setIsImportDialogOpen(true)
  }

  async function runEnrich(profiles: readonly LinkedInProfile[]) {
    const eligible = profiles.filter((p) => !p.is_enriched && p.apollo_id !== null)
    if (eligible.length === 0) return
    const apolloIds = eligible.map((p) => p.apollo_id!) as string[]

    setEnrichingIds((prev) => {
      const next = new Set(prev)
      for (const id of apolloIds) next.add(id)
      // Also flag the card id (preview's card id = apollo_id, so this is
      // belt-and-braces — keeping both in case the mapping ever changes).
      for (const p of eligible) next.add(p.id)
      return next
    })
    setEnrichError(null)
    setEnrichNotice(null)

    try {
      const result = await enrichApollo.mutateAsync({
        apollo_ids: apolloIds,
        user_config: userConfig,
      })

      // Splice enriched rows back into the result set by apollo_id.
      setEnrichedOverrides((prev) => {
        const next = new Map(prev)
        for (const enriched of result.enriched) {
          if (enriched.apollo_id) next.set(enriched.apollo_id, enriched)
        }
        return next
      })

      // Enriched rows are auto-imported server-side, so they leave the
      // selection (they're already in the Leads database).
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const p of eligible) next.delete(p.id)
        return next
      })

      // Confirm the durable outcome so an interruption later (reload, rate
      // limit, closed tab) can't make the user think their credits vanished.
      if (result.auto_imported > 0) {
        setEnrichNotice(
          `${result.auto_imported} lead${result.auto_imported === 1 ? '' : 's'} enriched and saved to your Leads page.` +
            (result.already_owned > 0 ? ` ${result.already_owned} already in your database (not charged).` : ''),
        )
      } else if (result.already_owned > 0 && result.matched > 0) {
        setEnrichNotice(
          `${result.already_owned} of these leads ${result.already_owned === 1 ? 'is' : 'are'} already in your database. No credits charged for them.`,
        )
      }

      if (result.skipped_at_capacity > 0) {
        setEnrichError(
          `${result.skipped_at_capacity} enriched lead${result.skipped_at_capacity === 1 ? '' : 's'} could not be saved because your plan's lead database is full. Archive old leads or upgrade, then import them from this page.`,
        )
      } else if (result.matched < result.requested) {
        setEnrichError(
          `${result.matched} of ${result.requested} leads enriched. The rest had stale records and were not charged.`,
        )
      }
    } catch (err) {
      setEnrichError(err instanceof Error ? err.message : 'Enrichment failed.')
    } finally {
      setEnrichingIds((prev) => {
        const next = new Set(prev)
        for (const id of apolloIds) next.delete(id)
        for (const p of eligible) next.delete(p.id)
        return next
      })
    }
  }

  function handleEnrichSingle(profile: LinkedInProfile) {
    void runEnrich([profile])
  }

  function handleBulkEnrich() {
    const selected = results.filter(
      (r) => selectedIds.has(r.id) && !r.is_enriched && r.apollo_id !== null,
    )
    void runEnrich(selected)
  }

  async function handleConfirmImport() {
    if (pendingImport.length === 0) return
    const optimisticIds = pendingImport.map((p) => p.id)
    // Flip the cards/rows to "Added" instantly. Roll back if the import
    // request fails so the user can retry without ghost-added rows.
    setImportedIds((prev) => {
      const next = new Set(prev)
      for (const id of optimisticIds) next.add(id)
      return next
    })
    try {
      let createdIds: readonly string[] = []
      if (pendingImport.length === 1 && pendingImport[0]) {
        const lead = await importLead.mutateAsync(profileToNewLead(pendingImport[0]))
        // Auto-fire deep enrichment (Perplexity + Claude). Per Eli's
        // directive this is free for the user — /ai/enrich now calls
        // trackTokens with chargeCredit=false so the user balance stays
        // untouched. Fire-and-forget so the dialog closes immediately.
        if (lead?.id) {
          createdIds = [lead.id]
          void apiFetch('/api/ai/enrich', { method: 'POST', body: { lead_id: lead.id } })
        }
      } else {
        const result = await bulkImport.mutateAsync(pendingImport.map(profileToNewLead))
        // Bulk path skips per-lead enrichment to avoid hammering the AI
        // worker with N parallel /ai/enrich calls. Users can run
        // enrichment per lead from the detail panel after import.
        createdIds = result.ids
      }
      setIsImportDialogOpen(false)
      setPendingImport([])
      setSelectedIds(new Set())
      // Offer to file the just-imported leads into a campaign right away.
      if (createdIds.length > 0) {
        setImportedLeadIds(createdIds)
        setIsAddCampaignOpen(true)
      }
    } catch {
      // Roll back the optimistic "Added" flips so the user can retry.
      setImportedIds((prev) => {
        const next = new Set(prev)
        for (const id of optimisticIds) next.delete(id)
        return next
      })
      // error is exposed via importLead.error / bulkImport.error for the dialog to display
    }
  }

  function handleCancelImport() {
    setIsImportDialogOpen(false)
    setPendingImport([])
  }

  function handleContinueSearch(filters: SearchFilters) {
    setActiveFilters(filters)
    setCursor(null)
    setHasSearched(true)
  }

  function handleClearHistory() {
    clearSearchHistory()
    setSearchHistory([])
  }

  function handleLoadMore() {
    setCursor(nextCursor)
  }

  const isImporting = importLead.isPending || bulkImport.isPending

  // --- Filter sidebar content (shared between desktop sidebar and mobile sheet) ---
  const filterSidebar = (
    <SearchFiltersPanel
      onSearch={handleSearch}
      isLoading={isLoading}
      initialFilters={initialFilters}
    />
  )

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Find Leads"
        subtitle="Discover LinkedIn profiles matching your ideal customer profile."
        actions={
          <div className="flex items-center gap-2">
            <SectionHelp sectionKey="lead-discovery" />
            {/* Mobile-only: Filters button opens Sheet */}
            <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2 md:hidden">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Search Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6">{filterSidebar}</div>
            </SheetContent>
          </Sheet>
          </div>
        }
      />

      <div className="flex gap-6">
        {/* Desktop filter sidebar */}
        <aside className="hidden w-[280px] shrink-0 md:block" data-tour="discover-filters">
          <div className="rounded-xl border bg-card p-5 shadow">
            <h3 className="mb-4 font-semibold text-sm">Search Filters</h3>
            {filterSidebar}
          </div>
        </aside>

        {/* Results area */}
        <div className="min-w-0 flex-1 space-y-4">
          {isDemoMode && searchNote && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">Demo mode.</strong> {searchNote}
              </span>
            </div>
          )}
          {!isDemoMode && searchNote && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{searchNote}</span>
            </div>
          )}
          {apolloError && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">Preview search unavailable:</strong> {apolloError}
              </span>
            </div>
          )}
          <CreditBar />
          {enrichNotice && (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-3 text-sm text-emerald-800 dark:text-emerald-200">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{enrichNotice}</span>
            </div>
          )}
          {enrichError && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{enrichError}</span>
            </div>
          )}
          <SearchResults
            results={results}
            isLoading={isLoading}
            hasSearched={hasSearched}
            selectedIds={selectedIds}
            enrichingIds={enrichingIds}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
            onImport={handleImportSingle}
            onEnrich={handleEnrichSingle}
            onBulkImport={handleBulkImport}
            onBulkEnrich={handleBulkEnrich}
            onLoadMore={handleLoadMore}
            hasNextPage={!!nextCursor}
          />

          {/* Search history — shown below results on first load */}
          {!hasSearched && (
            <SearchHistory
              history={searchHistory}
              onContinue={handleContinueSearch}
              onClear={handleClearHistory}
            />
          )}
        </div>
      </div>

      {/* Import confirmation dialog */}
      <ImportConfirmDialog
        profiles={pendingImport}
        isOpen={isImportDialogOpen}
        isLoading={isImporting}
        error={(importLead.error ?? bulkImport.error)?.message}
        onConfirm={handleConfirmImport}
        onCancel={handleCancelImport}
      />

      {/* Post-import: offer to add the freshly-imported leads to a campaign. */}
      <AddToCampaignDialog
        leadIds={importedLeadIds}
        open={isAddCampaignOpen}
        onOpenChange={(open) => {
          setIsAddCampaignOpen(open)
          if (!open) setImportedLeadIds([])
        }}
      />
    </div>
  )
}
